import type { PayloadAction } from '@reduxjs/toolkit';
import type { Except } from 'type-fest';

import { newKey } from '@ricoh/ria.client.core/utils/data/key';
import { isType } from '@ricoh/ria.client.core/utils/data/object';

import type { LassoFieldGeneral } from '~/common/logic/feature/process';
import type { Document, Page, PageDuplicate } from '~/common/model/api/batch';
import type { OperationLog } from '~/common/model/api/operation-log';

import processLogic from '~/common/logic/feature/process';
import fieldLogic from '~/common/logic/feature/settings/batch-type-library/field';

import type {
  BatchAddDocumentsFromSessionsPayload,
  BatchRemoveOCRFieldRowPositions,
  BatchSetBatchPayload,
  BatchSetDocumentTypePayload,
  BatchSetOCRPositionPayload,
  BatchSetPriorityPayload,
  DocumentMergeDocumentToPayload,
  DocumentMoveDocumentToPayload,
  PageAddPagePayload as PageAppendPagesPayload,
  PageDuplicatePagePayload,
  PageInsertPagesBeforePayload,
  PageInsertPagesHerePayload,
  PageMovePageToPayload,
  PageMoveToDocument,
  PageReplacePagePayload,
  PageSplitDocumentHerePayload,
  State,
} from './_batch.type';

/** Batch State. */
export const batchState: State = {
  batch: {
    priority: 'normal',

    addedPages: [],
    excludePages: [],

    dirty: false,

    operationLogs: [],
  },
};

/** Batch Reducer. */
export const batchReducer = {
  // #region -  Batch

  /**
   * Batch reset.
   *
   * @param {State} state State.
   */
  batch_reset: (state: State) => {
    state.batch.initialData = undefined;
    state.batch.data = undefined;

    state.batch.priority = 'normal';
    state.batch.targetDocumentId = undefined;
    state.batch.addedPages = [];
    state.batch.excludePages = [];

    state.batch.dirty = false;

    state.batch.operationLogs = [];
  },

  /**
   * Batch setBatch.
   *
   * @param {State} state State.
   * @param {PayloadAction<BatchSetBatchPayload>} action Action.
   */
  batch_setBatch: (state: State, { payload: { batch, documentNamePrefix, shouldDirty } }: PayloadAction<BatchSetBatchPayload>) => {
    state.batch.initialData = batch;

    // * Auto naming for documents
    let currentDocumentNumber = processLogic.document.getCurrentDocumentNumber(batch, documentNamePrefix);
    batch.documents.forEach((document) => {
      if (document.name) {
        return;
      }

      currentDocumentNumber += 1;
      document.name = `${documentNamePrefix} ${currentDocumentNumber}`;
    });

    state.batch.data = batch;
    state.batch.priority = batch.priority;
    state.batch.batchType = batch.type.name;
    state.batch.targetDocumentId = batch.documents.at(0)?.id;
    state.batch.addedPages = batch.documents.flatMap((document) => document.pages);
    state.batch.excludePages = [];

    state.batch.dirty = shouldDirty ? true : batch.status === 'initial';

    state.batch.operationLogs = [];
  },

  /**
   * Batch setBatchType.
   *
   * @param {State} state State.
   * @param {PayloadAction<string | undefined>} action Action.
   */
  batch_setBatchType: (state: State, { payload }: PayloadAction<string | undefined>) => {
    state.batch.batchType = payload;

    // * No need set dirty when batchType is changed. Because business logic not allow to change batchType after batch created.
  },

  /**
   * Batch setDocument.
   *
   * @param {State} state State.
   * @param {PayloadAction<Document>} action Action.
   */
  batch_setDocument: (state: State, { payload }: PayloadAction<Document>) => {
    state.batch.targetDocumentId = payload.id;

    // * No need set dirty when document is changed. Because this is just for UI for switching document.
  },

  /**
   * Batch setPriority.
   *
   * @param {State} state State.
   * @param {PayloadAction<BatchSetPriorityPayload>} action Action.
   */
  batch_setPriority: (state: State, { payload }: PayloadAction<BatchSetPriorityPayload>) => {
    if (state.batch.priority === payload.priority) {
      return;
    }

    state.batch.priority = payload.priority;

    if (payload.shouldDirty ?? true) {
      state.batch.dirty = true;
      state.batch.operationLogs.push({
        batch: 'updatedPriority',
        timestamp: Date.now(),
      });
    }
  },

  /**
   * Batch setDocumentType.
   *
   * @param {State} state State.
   * @param {PayloadAction<BatchSetDocumentTypePayload>} action Action.
   */
  batch_setDocumentType: (state: State, { payload }: PayloadAction<BatchSetDocumentTypePayload>) => {
    if (!state.batch.data) {
      return;
    }

    const targetDocument = state.batch.data.documents.find((item) => item.id === payload.id);
    if (!targetDocument) {
      return;
    }

    if ((targetDocument.type ?? '') === (payload.type ?? '')) {
      return;
    }

    targetDocument.type = payload.type;

    if (payload.shouldDirty ?? true) {
      state.batch.dirty = true;
      state.batch.operationLogs.push({
        document: 'updatedDocumentType',
        timestamp: Date.now(),
      });
    }
  },

  /**
   * Batch setOCRPosition.
   *
   * @param {State} state State.
   * @param {PayloadAction<BatchSetOCRPositionPayload>} action Action.
   */
  batch_setOCRPosition: (state: State, { payload }: PayloadAction<BatchSetOCRPositionPayload>) => {
    if (!state.batch.data) {
      return;
    }

    const targetDocument = state.batch.data.documents.find((document) => document.id === state.batch.targetDocumentId);
    if (!targetDocument) {
      return;
    }

    const targetField = targetDocument.fields.find(({ id }) => id === payload.lassoField.field.id);
    if (!targetField) {
      return;
    }

    if (isType<LassoFieldGeneral>(payload.lassoField, 'general')) {
      targetField.ocr.positions = [payload.position];
    } else {
      // * Different field type.
      if (processLogic.ocr.isPositionsGeneral(targetField.type, targetField.ocr.positions)) {
        return;
      }

      // * Not found row
      const rowPositions = targetField.ocr.positions.at(payload.lassoField.rowIndex);
      if (!rowPositions) {
        return;
      }

      rowPositions[payload.lassoField.columnIndex] = [payload.position];
    }

    state.batch.dirty = true;
  },

  /**
   * Batch clearOCRPosition.
   *
   * @param {State} state State.
   * @param {PayloadAction<string>} action Action.
   */
  batch_clearOCRPosition: (state: State, { payload }: PayloadAction<string>) => {
    if (!state.batch.data) {
      return;
    }

    const targetDocument = state.batch.data.documents.find((document) => document.id === state.batch.targetDocumentId);
    if (!targetDocument) {
      return;
    }

    const targetField = targetDocument.fields.find(({ id }) => id === payload);
    if (!targetField) {
      return;
    }

    targetField.ocr.positions = [];

    state.batch.dirty = true;
  },

  /**
   * Batch addOCRFieldRowPositions.
   *
   * @param {State} state State.
   * @param {PayloadAction<string>} action Action.
   */
  batch_addOCRFieldRowPositions: (state: State, { payload }: PayloadAction<string>) => {
    if (!state.batch.data) {
      return;
    }

    const targetDocument = state.batch.data.documents.find((document) => document.id === state.batch.targetDocumentId);
    if (!targetDocument) {
      return;
    }

    const targetField = targetDocument.fields.find(({ id }) => id === payload);
    if (!targetField) {
      return;
    }

    if (processLogic.ocr.isPositionsGeneral(targetField.type, targetField.ocr.positions) || !fieldLogic.isDataType.table(targetField.type)) {
      return;
    }

    targetField.ocr.positions.push(
      targetField.type.columns?.reduce(
        (prev, _column, index) => ({
          ...prev,
          [index]: [],
        }),
        {},
      ) ?? {},
    );

    state.batch.dirty = true;
  },

  /**
   * Batch removeOCRFieldRowPositions.
   *
   * @param {State} state State.
   * @param {PayloadAction<BatchRemoveOCRFieldRowPositions>} action Action.
   */
  batch_removeOCRFieldRowPositions: (state: State, { payload }: PayloadAction<BatchRemoveOCRFieldRowPositions>) => {
    if (!state.batch.data) {
      return;
    }

    const targetDocument = state.batch.data.documents.find((document) => document.id === state.batch.targetDocumentId);
    if (!targetDocument) {
      return;
    }

    const targetField = targetDocument.fields.find(({ id }) => id === payload.fieldId);
    if (!targetField) {
      return;
    }

    if (processLogic.ocr.isPositionsGeneral(targetField.type, targetField.ocr.positions) || !fieldLogic.isDataType.table(targetField.type)) {
      return;
    }

    targetField.ocr.positions.splice(payload.rowIndex, 1);

    state.batch.dirty = true;
  },

  /**
   * Batch add documents from sessions.
   *
   * @param {State} state State.
   * @param {PayloadAction<BatchAddDocumentsFromSessionsPayload>} action Action.
   */
  batch_addDocumentsFromSessions: (state: State, { payload }: PayloadAction<BatchAddDocumentsFromSessionsPayload>) => {
    if (!state.batch.data) {
      return;
    }

    // * Just add document if has not been added (its pages are not in addedPages)
    const addDocumentsPredicate = (document: Document): boolean =>
      !state.batch.addedPages.some((addedPage) => document.pages.some((page) => page.id === addedPage.id));

    payload.sessionDocuments.forEach((documents) => {
      if (!state.batch.data) {
        return;
      }

      const addedDocuments = processLogic.batch.addDocuments(state.batch.data, documents, payload.documentNamePrefix, addDocumentsPredicate);
      state.batch.addedPages = state.batch.addedPages.concat(addedDocuments.flatMap((document) => document.pages));

      state.batch.targetDocumentId ??= state.batch.data.documents.at(0)?.id;

      state.batch.dirty = true;

      if (addedDocuments.length) {
        state.batch.operationLogs.push({
          document: 'addedDocument',
          timestamp: Date.now(),
        });
      }
    });
  },

  /**
   * Batch setDirty.
   *
   * @param {State} state State.
   * @param {PayloadAction<boolean>} action Action.
   */
  batch_setDirty: (state: State, { payload }: PayloadAction<boolean>) => {
    state.batch.dirty = payload;
  },

  /**
   * Batch resetOperationLogs.
   *
   * @param {State} state State.
   */
  batch_resetOperationLogs: (state: State) => {
    state.batch.operationLogs = [];
  },

  // #endregion

  // #region - Document

  /**
   * Delete document.
   *
   * @param {State} state State.
   * @param {PayloadAction<Document>} action Action.
   */
  document_deleteDocument: (state: State, { payload }: PayloadAction<Document>) => {
    if (!state.batch.data) {
      return;
    }

    state.batch.data.documents = state.batch.data.documents.filter((document) => document.id !== payload.id);

    state.batch.targetDocumentId = state.batch.data.documents.at(0)?.id;

    state.batch.dirty = true;

    state.batch.operationLogs.push({
      document: 'deletedDocument',
      timestamp: Date.now(),
    });
  },

  /**
   * Merge document to.
   *
   * @param {State} state State.
   * @param {PayloadAction<DocumentMergeDocumentToPayload>} action Action.
   */
  document_mergeDocumentTo: (state: State, { payload }: PayloadAction<DocumentMergeDocumentToPayload>) => {
    if (!state.batch.data) {
      return;
    }

    const targetDocument = state.batch.data.documents.find((document) => document.id === payload.mergeToDocument.id);
    if (!targetDocument) {
      return;
    }

    targetDocument.pages.push(...payload.document.pages);

    state.batch.data.documents = state.batch.data.documents.filter((document) => document.id !== payload.document.id);

    state.batch.targetDocumentId = targetDocument.id;

    state.batch.dirty = true;

    state.batch.operationLogs.push({
      document: 'mergedDocument',
      timestamp: Date.now(),
    });
  },

  /**
   * Move document to.
   *
   * @param {State} state State.
   * @param {PayloadAction<DocumentMoveDocumentToPayload>} action Action.
   */
  document_moveDocumentTo: (state: State, { payload }: PayloadAction<DocumentMoveDocumentToPayload>) => {
    if (!state.batch.data) {
      return;
    }

    const documentIndex = state.batch.data.documents.findIndex((document) => document.id === payload.document.id);
    if (documentIndex < 0) {
      return;
    }

    let prevDocuments = state.batch.data.documents.slice(0, payload.index + 0.5);
    let nextDocuments = state.batch.data.documents.slice(payload.index + 0.5);

    if (documentIndex < payload.index) {
      prevDocuments = prevDocuments.filter((document) => document.id !== payload.document.id);
    } else {
      nextDocuments = nextDocuments.filter((document) => document.id !== payload.document.id);
    }

    state.batch.data.documents = [...prevDocuments, payload.document, ...nextDocuments];

    state.batch.targetDocumentId = payload.document.id;

    state.batch.dirty = true;

    state.batch.operationLogs.push({
      document: 'movedDocument',
      timestamp: Date.now(),
    });
  },

  // #endregion

  // #region - Page

  /**
   * Insert pages here.
   *
   * @param {State} state State.
   * @param {PayloadAction<PageInsertPagesHerePayload>} action Action.
   */
  page_insertPagesHere: (state: State, { payload }: PayloadAction<PageInsertPagesHerePayload>) => {
    if (!state.batch.data) {
      return;
    }

    const targetDocument = state.batch.data.documents.find((document) => document.id === state.batch.targetDocumentId);
    if (!targetDocument) {
      return;
    }

    const pageIndex = targetDocument.pages.findIndex((page) => page.id === payload.page.id);
    if (pageIndex < 0) {
      return;
    }

    targetDocument.pages.splice(pageIndex + 1, 0, ...payload.pages);

    state.batch.dirty = true;

    state.batch.operationLogs.push({
      page: 'addedPage',
      timestamp: Date.now(),
    });
  },

  /**
   * Split Document here.
   *
   * @param {State} state State.
   * @param {PayloadAction<PageSplitDocumentHerePayload>} action Action.
   */
  page_splitDocumentHere: (state: State, { payload }: PayloadAction<PageSplitDocumentHerePayload>) => {
    if (!state.batch.data) {
      return;
    }

    const documentIndex = state.batch.data.documents.findIndex((document) => document.id === state.batch.targetDocumentId);
    if (documentIndex < 0) {
      return;
    }

    const targetDocument = state.batch.data.documents.at(documentIndex);
    if (!targetDocument) {
      return;
    }

    const pageIndex = targetDocument.pages.findIndex((page) => page.id === payload.page.id);
    if (pageIndex < 0) {
      return;
    }

    const prevPages = targetDocument.pages.slice(0, pageIndex + 1);
    const nextPages = targetDocument.pages.slice(pageIndex + 1);

    const newDocument: Document = {
      id: newKey(),
      name: `${payload.documentNamePrefix} ${processLogic.document.getCurrentDocumentNumber(state.batch.data, payload.documentNamePrefix) + 1}`,
      type: targetDocument.type,
      sourceType: targetDocument.sourceType,
      pages: nextPages,
      fields: [],
      stamps: [],
    };

    targetDocument.pages = prevPages;
    state.batch.data.documents.splice(documentIndex + 1, 0, newDocument);

    state.batch.dirty = true;

    state.batch.operationLogs.push({
      document: 'splitDocument',
      timestamp: Date.now(),
    });
  },

  /**
   * Replace page.
   *
   * @param {State} state State.
   * @param {PayloadAction<PageReplacePagePayload>} action Action.
   */
  page_replacePage: (state: State, { payload }: PayloadAction<PageReplacePagePayload>) => {
    if (!state.batch.data) {
      return;
    }

    const targetDocument = state.batch.data.documents.find((document) => document.id === state.batch.targetDocumentId);
    if (!targetDocument) {
      return;
    }

    const pageIndex = targetDocument.pages.findIndex((page) => page.id === payload.page.id);
    if (pageIndex < 0) {
      return;
    }

    targetDocument.pages.splice(pageIndex, 1, ...payload.pages);

    state.batch.dirty = true;

    state.batch.operationLogs.push({
      page: 'replacedPage',
      timestamp: Date.now(),
    });
  },

  /**
   * Insert pages before.
   *
   * @param {State} state State.
   * @param {PayloadAction<PageInsertPagesBeforePayload>} action Action.
   */
  page_insertPagesBefore: (state: State, { payload }: PayloadAction<PageInsertPagesBeforePayload>) => {
    if (!state.batch.data) {
      return;
    }

    const targetDocument = state.batch.data.documents.find((document) => document.id === state.batch.targetDocumentId);
    if (!targetDocument) {
      return;
    }

    const pageIndex = targetDocument.pages.findIndex((page) => page.id === payload.page.id);
    if (pageIndex < 0) {
      return;
    }

    targetDocument.pages.splice(pageIndex, 0, ...payload.pages);

    state.batch.dirty = true;

    state.batch.operationLogs.push({
      page: 'addedPage',
      timestamp: Date.now(),
    });
  },

  /**
   * Delete page.
   *
   * @param {State} state State.
   * @param {PayloadAction<Page>} action Action.
   */
  page_delete: (state: State, { payload }: PayloadAction<Page>) => {
    if (!state.batch.data) {
      return;
    }

    const documentIndex = state.batch.data.documents.findIndex((document) => document.id === state.batch.targetDocumentId);
    if (documentIndex < 0) {
      return;
    }

    const targetDocument = state.batch.data.documents.at(documentIndex);
    if (!targetDocument) {
      return;
    }

    targetDocument.pages = targetDocument.pages.filter((page) => {
      if (page.id !== payload.id) {
        return true;
      }

      // * Just delete page if not have duplicated pages. Otherwise, exclude page.
      if (page.hasDuplicated) {
        page.isExcluded = true;
        state.batch.excludePages.push(page);
      }

      return false;
    });

    // * Delete document if no pages. -> Delete document
    if (targetDocument.pages.length === 0) {
      state.batch.data.documents = state.batch.data.documents.filter((document) => document.id !== targetDocument.id);
      state.batch.targetDocumentId = state.batch.data.documents.at(documentIndex)?.id ?? state.batch.data.documents.at(0)?.id;
    }

    state.batch.dirty = true;

    state.batch.operationLogs.push({
      page: 'deletedPage',
      timestamp: Date.now(),
    });
  },

  /**
   * Duplicate page.
   *
   * @param {State} state State.
   * @param {PayloadAction<PageDuplicatePagePayload>} action Action.
   */
  page_duplicatePage: (state: State, { payload }: PayloadAction<PageDuplicatePagePayload>) => {
    if (!state.batch.data) {
      return;
    }

    const duplicateToDocument = state.batch.data.documents.find((document) => document.id === payload.duplicateToDocument.id);
    if (!duplicateToDocument) {
      return;
    }

    const targetDocument = state.batch.data.documents.find((document) => document.id === state.batch.targetDocumentId);
    if (!targetDocument) {
      return;
    }

    const targetPage = targetDocument.pages.find((page) => page.id === payload.page.id);
    if (!targetPage) {
      return;
    }

    targetPage.hasDuplicated = true;

    const duplicatePage: PageDuplicate = {
      $is: 'duplicate',
      id: payload.id,
      rotation: targetPage.rotation,
      duplicateFrom: targetPage,
    };
    duplicateToDocument.pages.unshift(duplicatePage);

    state.batch.dirty = true;

    state.batch.operationLogs.push({
      page: 'duplicatedPage',
      timestamp: Date.now(),
    });
  },

  /**
   * Move page to Document.
   *
   * @param {State} state State.
   * @param {PayloadAction<PageMoveToDocument>} action Action.
   */
  page_moveToDocument: (state: State, { payload }: PayloadAction<PageMoveToDocument>) => {
    if (!state.batch.data) {
      return;
    }

    const moveToDocument = state.batch.data.documents.find((document) => document.id === payload.moveToDocument.id);
    if (!moveToDocument) {
      return;
    }

    const targetDocument = state.batch.data.documents.find((document) => document.id === state.batch.targetDocumentId);
    if (!targetDocument) {
      return;
    }

    moveToDocument.pages.push(payload.page);
    targetDocument.pages = targetDocument.pages.filter((page) => page.id !== payload.page.id);

    // * Delete document if no pages.
    if (targetDocument.pages.length === 0) {
      state.batch.data.documents = state.batch.data.documents.filter((document) => document.id !== targetDocument.id);
      state.batch.targetDocumentId = moveToDocument.id;
    }

    state.batch.dirty = true;

    state.batch.operationLogs.push({
      page: 'movedPage',
      timestamp: Date.now(),
    });
  },

  /**
   * MoveUp page.
   *
   * @param {State} state State.
   * @param {PayloadAction<Page>} action Action.
   */
  page_moveUp: (state: State, { payload }: PayloadAction<Page>) => {
    if (!state.batch.data) {
      return;
    }

    const targetDocument = state.batch.data.documents.find((document) => document.id === state.batch.targetDocumentId);
    if (!targetDocument) {
      return;
    }

    const pageIndex = targetDocument.pages.findIndex((page) => page.id === payload.id);
    if (pageIndex < 1) {
      return;
    }

    const targetPage = targetDocument.pages.at(pageIndex);
    const prevPage = targetDocument.pages.at(pageIndex - 1);
    if (!targetPage || !prevPage) {
      return;
    }

    targetDocument.pages[pageIndex] = prevPage;
    targetDocument.pages[pageIndex - 1] = targetPage;

    state.batch.dirty = true;

    state.batch.operationLogs.push({
      page: 'movedPage',
      timestamp: Date.now(),
    });
  },

  /**
   * MoveDown page.
   *
   * @param {State} state State.
   * @param {PayloadAction<Page>} action Action.
   */
  page_moveDown: (state: State, { payload }: PayloadAction<Page>) => {
    if (!state.batch.data) {
      return;
    }

    const targetDocument = state.batch.data.documents.find((document) => document.id === state.batch.targetDocumentId);
    if (!targetDocument) {
      return;
    }

    const pageIndex = targetDocument.pages.findIndex((page) => page.id === payload.id);
    if (pageIndex + 1 >= targetDocument.pages.length) {
      return;
    }

    const targetPage = targetDocument.pages.at(pageIndex);
    const nextPage = targetDocument.pages.at(pageIndex + 1);
    if (!targetPage || !nextPage) {
      return;
    }

    targetDocument.pages[pageIndex] = nextPage;
    targetDocument.pages[pageIndex + 1] = targetPage;

    state.batch.dirty = true;

    state.batch.operationLogs.push({
      page: 'movedPage',
      timestamp: Date.now(),
    });
  },

  /**
   * Rotate page.
   *
   * @param {State} state State.
   * @param {PayloadAction<Page>} action Action.
   */
  page_rotate: (state: State, { payload }: PayloadAction<Page>) => {
    if (!state.batch.data) {
      return;
    }
    const targetDocument = state.batch.data.documents.find((document) => document.id === state.batch.targetDocumentId);
    if (!targetDocument) {
      return;
    }

    const pageIndex = targetDocument.pages.findIndex((page) => page.id === payload.id);
    if (pageIndex < 0) {
      return;
    }

    const targetPage = targetDocument.pages.at(pageIndex);
    if (!targetPage) {
      return;
    }

    switch (targetPage.rotation) {
      case 'counterclockwise':
        targetPage.rotation = 'upsideDown';
        break;

      case 'upsideDown':
        targetPage.rotation = 'clockwise';
        break;

      case 'clockwise':
        targetPage.rotation = 'noRotation';
        break;

      case 'noRotation':
      default:
        targetPage.rotation = 'counterclockwise';
        break;
    }

    state.batch.dirty = true;

    state.batch.operationLogs.push({
      page: 'rotatedPage',
      timestamp: Date.now(),
    });
  },

  /**
   * Move page to.
   *
   * @param {State} state State.
   * @param {PayloadAction<PageMovePageToPayload>} action Action.
   */
  page_movePageTo: (state: State, { payload }: PayloadAction<PageMovePageToPayload>) => {
    if (!state.batch.data) {
      return;
    }

    const targetDocument = state.batch.data.documents.find((document) => document.id === state.batch.targetDocumentId);
    if (!targetDocument) {
      return;
    }

    const pageIndex = targetDocument.pages.findIndex((page) => page.id === payload.page.id);
    if (pageIndex < 0) {
      return;
    }

    let prevPages = targetDocument.pages.slice(0, payload.index + 0.5);
    let nextPages = targetDocument.pages.slice(payload.index + 0.5);

    if (pageIndex < payload.index) {
      prevPages = prevPages.filter((page) => page.id !== payload.page.id);
    } else {
      nextPages = nextPages.filter((page) => page.id !== payload.page.id);
    }

    targetDocument.pages = [...prevPages, payload.page, ...nextPages];

    state.batch.dirty = true;

    state.batch.operationLogs.push({
      page: 'movedPage',
      timestamp: Date.now(),
    });
  },

  /**
   * Append pages.
   *
   * @param {State} state State.
   * @param {PayloadAction<PageAppendPagesPayload>} action Action.
   */
  page_appendPages: (state: State, { payload }: PayloadAction<PageAppendPagesPayload>) => {
    if (!state.batch.data) {
      return;
    }

    const targetDocument = state.batch.data.documents[payload.index] ?? {
      id: newKey(),
      name: `${payload.documentNamePrefix} ${processLogic.document.getCurrentDocumentNumber(state.batch.data, payload.documentNamePrefix) + 1}`,
      type: payload.type,
      sourceType: payload.sourceType,
      pages: [],
      fields: [],
      stamps: [],
    };

    payload.pages.forEach(({ index, page }) => {
      targetDocument.pages[index] = page;
    });
    state.batch.data.documents[payload.index] = targetDocument;

    state.batch.targetDocumentId ??= state.batch.data.documents.at(0)?.id;
  },

  // #endregion

  // #region Operation logs

  /**
   * Push operation log.
   *
   * @param {State} state State.
   * @param {PayloadAction<Except<OperationLog, 'timestamp'>>} action Action.
   */
  operationLogs_push: (state: State, { payload }: PayloadAction<Except<OperationLog, 'timestamp'>>) => {
    state.batch.operationLogs.push({
      ...payload,
      timestamp: Date.now(),
    });
  },

  // #endregion
};
